import {type Moment} from 'moment';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';

import {Button, EMPHASIS, FlexRow, IconTooltip, SegmentedControl, Tabs} from '@forecast-it/design-system';

import HeaderLabel, {INDICATOR_COLOR} from '../../../../components/new-ui/HeaderLabel';
import {APPROVAL_STATUS, ApprovalStatus, GLOBAL_FILTER_FIELD, GLOBAL_FILTER_OPERATOR} from '../../../../constants';
import {EVENT_ID} from '../../../../containers/event_manager';
import useSubscription from '../../../../forecast-app/shared/hooks/useSubscription';
import {ForecastQueryRenderer} from '../../../../ForecastQueryRenderer';
import {RelayProps} from '../../../../relay-helpers/TSRelayConverter';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import {trackTimeRegistrationSuggestionsShown} from '../../../../tracking/tracking_types/TrackTimeRegistrationSuggestionsShown';
import TheEye from '../../../shared/components/headers/header-bar/TheEye';
import InlineLoader from '../../../shared/components/inline-loader/inline_loader';
import ForecastTooltip from '../../../shared/components/tooltips/ForecastTooltip';
import {useForecastFetchQuery} from '../../../shared/hooks/useForecastFetchQuery';
import Util from '../../../shared/util/util';
import {TimeRegistrationTargetSuggstionsData} from '../time-registration-target-suggestions/TimeRegistrationTargetSuggestionsFetch';
import {getValidSuggestions} from '../time-registration-target-suggestions/TimeRegistrationTargetSuggestionUtil';
import {TIME_PERIOD, type TimePeriod} from '../timesheets_change_date_view';
import {getHolidaysOfTheDay} from '../timesheets_person_data';
import {TIMESHEET_THIS_WEEKS_WORK_PAGE_SIZE} from '../Constants';
import {
	BottomSectionWrapper,
	DateInfoSectionWrapper,
	InfoSectionWrapper,
	TabSectionWrapper,
	TableWrapper,
} from '../timesheets.styled';
import {LOCALSTORAGE_TIMESHEETS_SELECTED_TAB} from '../top-section/TimesheetsCalendarUtil';
import type {TimesheetsFilters_viewer$data as TimesheetsFilters_viewer} from './__generated__/TimesheetsFilters_viewer.graphql';
import {addTimeRegTypes, groupRegs} from './timesheets-time-registration-table/timesheet-table-util/DataUtil';
import type {TimesheetsTaskCountQuery$data as TimesheetsTaskCountQueryResponse} from './timesheets-time-registration-table/timesheet-table-wrapper/__generated__/TimesheetsTaskCountQuery.graphql';
import {TimesheetsTaskCountQuery} from './timesheets-time-registration-table/timesheet-table-wrapper/TimesheetsTaskCountQuery';
import TimesheetThisWeeksWorkTableWrapper, {
	TimesheetThisWeeksWorkTableWrapperQuery,
} from './timesheets-time-registration-table/timesheet-table-wrapper/TimesheetThisWeeksWorkTableWrapper';
import TimesheetTimeTableWrapper, {
	TimesheetTimeTableWrapperQuery,
} from './timesheets-time-registration-table/timesheet-table-wrapper/TimesheetTimeTableWrapper';
import {DefaultFiltersType, TimesheetsFilters} from './TimesheetsFilters';
import {suggestionsWithoutTimeRegs} from './TimesheetUtil';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import {
	hasFeatureFlag,
	usingInternalTimeApproval as isUsingInternalTimeApproval,
	usingTimeApproval as isUsingTimeApproval,
} from '../../../shared/util/FeatureUtil';
import {getApprovalStatusForWeek} from '@/forecast-app/shared/util/TimesheetApprovalStatus';
import type {TimesheetThisWeeksWorkTableWrapper_viewer$data as TimesheetThisWeeksWorkTableWrapper_viewer} from './timesheets-time-registration-table/timesheet-table-wrapper/__generated__/TimesheetThisWeeksWorkTableWrapper_viewer.graphql';
import type {TimesheetTimeTableWrapper_viewer$data as TimesheetTimeTableWrapper_viewer} from './timesheets-time-registration-table/timesheet-table-wrapper/__generated__/TimesheetTimeTableWrapper_viewer.graphql';
import {useTimeRegistrationSettings} from '@/forecast-app/shared/hooks/useTimeRegistrationSettings';
import TimesheetInternalTimeTableWrapper, {
	TimesheetInternalTimeTableWrapperQuery,
} from './timesheets-time-registration-table/timesheet-table-wrapper/TimesheetInternalTimeTableWrapper';
import type {TimesheetInternalTimeTableWrapper_viewer$data as TimesheetInternalTimeTableWrapper_viewer} from './timesheets-time-registration-table/timesheet-table-wrapper/__generated__/TimesheetInternalTimeTableWrapper_viewer.graphql';
import {SortableColumn, TimesheetColumnSort, usePersistentTimesheetColumnSort} from './usePersistentTimesheetColumnSort';
import {useStoredState} from '../../../shared/hooks/useStoredState';
import {useTimesheetColumnEyeOptions} from './timesheets-time-registration-table/useTimesheetColumnEyeOptions';

enum TimesheetTabName {
	FindTasks = 'FindTasks',
	Timesheet = 'Timesheet',
	InternalTime = 'InternalTime',
}

function isTimesheetTab(value: unknown): value is TimesheetTabName {
	return typeof value === 'string' && (Object.values(TimesheetTabName) as string[]).includes(value);
}

type TaskTypeSearchQuery = {
	filters: {
		field: keyof typeof GLOBAL_FILTER_FIELD;
		operator: keyof typeof GLOBAL_FILTER_OPERATOR;
		value: unknown[];
	}[];
};

const ColumnValueMap: Record<SortableColumn, string> = {
	[SortableColumn.ProjectName]: 'project-name',
	[SortableColumn.TaskName]: 'task-name',
	[SortableColumn.Phase]: 'phase-name',
	[SortableColumn.Client]: 'client-name',
	[SortableColumn.ProjectIndicator]: 'project-id',
	[SortableColumn.Star]: 'starred',
};
function serialiseSort(sort: TimesheetColumnSort): string {
	const valuePrefix = ColumnValueMap[sort.value];
	const directionSuffix = sort.ascending ? 'asc' : 'dsc';

	return `${valuePrefix}-${directionSuffix}`;
}

type ViewerType = {
	company: {
		id: string;
		harvestEnabled: boolean;
		unit4Enabled: boolean;
		internalTimes: {
			totalCount: number;
		};
		useTimeApproval: boolean;
		useInternalTimeApproval: boolean;
	};
	harvestUser: boolean;
	unit4User: boolean;
};

type TimesheetTabProps = {
	timeRegsForPeriod: unknown;
};
const TimesheetTab = ({timeRegsForPeriod}: TimesheetTabProps) => {
	const {formatMessage} = useIntl();

	// Group filtered timereg by type to show the row-count to make the number comparable to the "this weeks work" count.
	const groupedTimeRegs = groupRegs(addTimeRegTypes(timeRegsForPeriod));
	const timesheetItemsCount = groupedTimeRegs.size;

	return (
		<Tabs.Tab data-cy={'timesheet-tab'} value={TimesheetTabName.Timesheet}>
			<FormattedMessage id="timesheets.tabs.timesheets.title" values={{count: timesheetItemsCount}} />
			<IconTooltip>
				<IconTooltip.Item
					title={formatMessage({id: 'timesheets.tabs.timesheets.tooltip_title'})}
					details={formatMessage({id: 'timesheets.tabs.timesheet.tooltip'})}
				/>
			</IconTooltip>
		</Tabs.Tab>
	);
};

type FindTasksTabProps = {
	taskTypeSearchQuery: TaskTypeSearchQuery;
};
const FindTasksTab = ({taskTypeSearchQuery}: FindTasksTabProps) => {
	const {formatMessage} = useIntl();

	type TimesheetsTaskCountVariables = {taskTypeSearchQuery: typeof taskTypeSearchQuery};
	const {fetch, data} = useForecastFetchQuery<TimesheetsTaskCountQueryResponse, TimesheetsTaskCountVariables>(
		TimesheetsTaskCountQuery
	);
	const timeSheetsTaskCountData: TimesheetsTaskCountQueryResponse | null = data;
	useEffect(() => {
		if (taskTypeSearchQuery?.filters) {
			fetch({
				taskTypeSearchQuery: taskTypeSearchQuery,
			});
		}
	}, [taskTypeSearchQuery]);
	const thisWeeksWorkItemsCount = timeSheetsTaskCountData?.viewer?.company?.allTasksCount || 0;

	return (
		<Tabs.Tab data-cy={'this-weeks-work-tab'} value={TimesheetTabName.FindTasks}>
			<FormattedMessage id="timesheets.tabs.tasks.title" values={{count: thisWeeksWorkItemsCount}} />
			<IconTooltip>
				<IconTooltip.Item
					title={formatMessage({id: 'timesheets.tabs.tasks.tooltip_title'})}
					details={formatMessage({id: 'timesheets.tabs.task.tooltip'})}
				/>
			</IconTooltip>
		</Tabs.Tab>
	);
};

type InternalTimeTabProps = {
	viewer: ViewerType;
};

const InternalTimeTab = ({viewer}: InternalTimeTabProps) => {
	const {formatMessage} = useIntl();

	const {allowInternalTime} = useTimeRegistrationSettings();

	const internalTimesCount = viewer.company.internalTimes.totalCount;

	const showInternalTimeTab = allowInternalTime && internalTimesCount > 0;
	return showInternalTimeTab ? (
		<Tabs.Tab data-cy={'internal-time-tab'} value={TimesheetTabName.InternalTime}>
			<FormattedMessage id="timesheets.tabs.internal_time.title" />
			<IconTooltip>
				<IconTooltip.Item
					title={formatMessage({id: 'timesheets.tabs.internal_time.tooltip_title'})}
					details={formatMessage({id: 'timesheets.tabs.internal_time.tooltip'})}
				/>
			</IconTooltip>
		</Tabs.Tab>
	) : null;
};

type DateInfoSectionProps = {
	selectedTab: TimesheetTabName;
	currentViewingDate: Moment;
	weekStart: Moment;
	weekEnd: Moment;
	showAllTimeTasks: boolean;
	timePeriod: TimePeriod;
	holidays: {node: {name: string}}[];
};

const DateInfoSection = ({
	selectedTab,
	currentViewingDate,
	weekStart,
	weekEnd,
	showAllTimeTasks,
	timePeriod,
	holidays,
}: DateInfoSectionProps) => {
	const isWeekView = timePeriod === TIME_PERIOD.WEEK_VIEW;

	const period =
		selectedTab === TimesheetTabName.FindTasks && showAllTimeTasks
			? 'All Time'
			: isWeekView
			? `${weekStart.format('D')} - ${weekEnd.format('D MMMM')}`
			: currentViewingDate.format('dddd D MMMM');

	const infoText =
		selectedTab === TimesheetTabName.Timesheet ? (
			<FormattedMessage id={'timesheets.tabs.common.info_text'} values={{entity: 'time registrations', period}} />
		) : selectedTab === TimesheetTabName.FindTasks ? (
			<FormattedMessage id={'timesheets.tabs.common.info_text'} values={{entity: 'tasks', period}} />
		) : null;

	const showHolidays = selectedTab === TimesheetTabName.Timesheet && !isWeekView;

	return (
		<DateInfoSectionWrapper color="medium" size="2">
			{infoText}
			{showHolidays && holidays?.length > 0 && (
				<div className={'holiday-names'}>{`(${holidays.map(h => h.node.name).join(', ')})`}</div>
			)}
		</DateInfoSectionWrapper>
	);
};

type PeriodToggleProps = {
	timePeriod: TimePeriod;
	setTimePeriod: (period: TimePeriod) => void;
};

const PeriodToggle = ({timePeriod, setTimePeriod}: PeriodToggleProps) => {
	const {formatMessage} = useIntl();

	return (
		<SegmentedControl
			value={timePeriod}
			onValueChange={value => {
				setTimePeriod(value);
				trackEvent('Period Selector', 'Toggled', {timePeriod: value});
			}}
			data-cy={'period-toggle'}
		>
			<SegmentedControl.Item
				value={TIME_PERIOD.DAY_VIEW}
				label={formatMessage({id: 'overview_time.day'})}
				data-cy={'day'}
			>
				<FormattedMessage id="overview_time.day" />
			</SegmentedControl.Item>
			<SegmentedControl.Item
				value={TIME_PERIOD.WEEK_VIEW}
				label={formatMessage({id: 'overview_time.week'})}
				data-cy={'week'}
			>
				<FormattedMessage id="overview_time.week" />
			</SegmentedControl.Item>
		</SegmentedControl>
	);
};

// Parent supplying Relay info is still JS so keeping those props as implicit any. Comment indicates where the type would be found in a TS generated file.
type TimesheetsBottomSectionProps = {
	timeRegistrations; // TimesheetsPage_viewer['timeRegistrations']['edges']
	currentViewingDate: Moment;
	setCurrentViewingDate: (date: Moment) => void;
	timePeriod: TimePeriod;
	setTimePeriod: (period: TimePeriod) => void;
	selectedPerson; // TimesheetsPage_viewer['company']['person']
	lastWorkingDayInWeek; // unknown type
	nonViewerPersonSelected: boolean;
	lockedDate?: Moment;
	customFieldDefinitions; // TimesheetsPage_viewer['company']['customFieldDefinitions]['edges'][0]['node'];
	overBudgetProgramsByCompanyProjectId; // Custom type of {companyProjectId, program}
	timeRegistrationTargetSuggestionsData: TimeRegistrationTargetSuggstionsData;
	paginationRef;
	viewer: ViewerType & TimesheetsFilters_viewer;
	timeRegSideModalOpen: boolean;
	isCopyDisabled: boolean;
	selectedPersonId: string;
	filters: DefaultFiltersType;
	setFilters: (filters: DefaultFiltersType) => void;
	searchActive: boolean;
	taskTypeSearchQuery: TaskTypeSearchQuery;
	showAllTimeTasks: boolean;
	setShowAllTimeTasks: (filters: boolean) => void;
	refetch: () => void;
	setPeriodTimeApprovalStatus?: (status: ApprovalStatus) => void;
	setHasProjectTimeRegsInWeek?: (value: boolean) => void;
};

export const getTimesheetsApprovalStatus = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	timeRegs: any[],
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	person: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	company: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	weekViewDate: any,
	hasNewStatuses = false,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	personWeekApprovals: any[] = []
): ApprovalStatus => {
	// Adapt Relay edge array → flat node array for the shared helper
	const nodes = (timeRegs ?? []).map(e => e?.node ?? e);
	// PIKA-519: forward weekApprovals to enable synthesis precedence step 2
	// (PWA APPROVED/DECLINED sticky). Pre-PIKA-519 callers that don't pass the array
	// get the default [] and behave per pre-PIKA-519 precedence (lock-based only).
	return getApprovalStatusForWeek(
		person,
		company,
		weekViewDate,
		nodes,
		hasNewStatuses,
		personWeekApprovals
	) as ApprovalStatus;
};

export const TimesheetsBottomSection = ({
	timeRegistrations,
	currentViewingDate,
	setCurrentViewingDate,
	timePeriod,
	setTimePeriod,
	selectedPerson,
	nonViewerPersonSelected,
	lastWorkingDayInWeek,
	lockedDate,
	customFieldDefinitions,
	overBudgetProgramsByCompanyProjectId,
	timeRegistrationTargetSuggestionsData,
	paginationRef,
	viewer,
	timeRegSideModalOpen,
	isCopyDisabled,
	selectedPersonId,
	filters,
	setFilters,
	searchActive,
	taskTypeSearchQuery,
	showAllTimeTasks,
	setShowAllTimeTasks,
	refetch,
	setPeriodTimeApprovalStatus,
	setHasProjectTimeRegsInWeek,
}: TimesheetsBottomSectionProps): React.ReactElement => {
	const {formatMessage} = useIntl();
	const isWeekView = timePeriod === TIME_PERIOD.WEEK_VIEW;

	const defaultTab = useMemo(() => {
		const calendarFetchEnabled =
			viewer.calendarIntegrations?.googleCalendar?.calendarFetchEnabled ||
			viewer.calendarIntegrations?.outlookCalendar?.calendarFetchEnabled ||
			false;
		return calendarFetchEnabled ? TimesheetTabName.Timesheet : TimesheetTabName.FindTasks;
	}, [
		viewer.calendarIntegrations?.googleCalendar?.calendarFetchEnabled,
		viewer.calendarIntegrations?.outlookCalendar?.calendarFetchEnabled,
	]);

	const [selectedTab, setSelectedTab] = useStoredState<TimesheetTabName>(LOCALSTORAGE_TIMESHEETS_SELECTED_TAB, value => {
		return isTimesheetTab(value) ? value : defaultTab;
	});

	// If defaultTab changes, switch to that tab.
	const didMount = React.useRef(false);
	useEffect(() => {
		if (didMount.current) {
			setSelectedTab(defaultTab);
		} else {
			didMount.current = true;
		}
	}, [defaultTab]);

	const sort: TimesheetColumnSort = usePersistentTimesheetColumnSort();

	const [showSuggestions, setShowSuggestions] = React.useState(false);
	const [savedFilters, setSavedFilters] = useState<JSX.Element | null>(null);

	const {eyeOptions, setEyeOptions} = useTimesheetColumnEyeOptions();

	const {allowOutsideTaskDates} = useTimeRegistrationSettings();
	const usingTimeApproval = isUsingTimeApproval(viewer.company.useTimeApproval);
	const usingInternalTimeApproval = isUsingInternalTimeApproval(viewer.company.useInternalTimeApproval);

	const validTimeRegistrationSuggestions = useMemo(
		() => getValidSuggestions(timeRegistrationTargetSuggestionsData, selectedPerson.id),
		[timeRegistrationTargetSuggestionsData, selectedPerson.id]
	);
	const hasSuggestions: boolean =
		!nonViewerPersonSelected && !!validTimeRegistrationSuggestions && validTimeRegistrationSuggestions.length > 0;

	const handleTabChange = (value: string) => {
		if (isTimesheetTab(value)) {
			trackEvent('Timesheet Table', 'Tab Changed', {prevTab: selectedTab, newTab: value});
			setSelectedTab(value);
		}
	};

	const goToTimesheetsTab = () => {
		trackEvent('Timesheet Table Totals', 'Clicked');
		handleTabChange(TimesheetTabName.Timesheet);
	};

	const setAndTrackShowSuggestions = useCallback(
		(showSuggestions: boolean) => {
			if (showSuggestions) {
				const newSuggestions =
					timeRegistrationTargetSuggestionsData?.timeRegistrationTargetSuggestions?.sortedSuggestions;
				const newSuggestionsAvailable = newSuggestions && newSuggestions.length > 0;
				trackEvent('Time Registration Suggestions', 'Shown', {version: newSuggestionsAvailable ? 'New' : 'Old'});
				trackTimeRegistrationSuggestionsShown({
					companyId: viewer.company.id,
					personId: selectedPerson.id,
					modelVersion: timeRegistrationTargetSuggestionsData?.timeRegistrationTargetSuggestions?.modelVersion,
				});
			}
			setShowSuggestions(showSuggestions);
		},
		[timeRegistrationTargetSuggestionsData]
	);

	useSubscription(EVENT_ID.SHOW_TIME_REG_SUGGESTIONS, () => {
		trackEvent('Show Suggestions Link', 'Clicked', {availableSuggestions: validTimeRegistrationSuggestions?.length});
		setAndTrackShowSuggestions(true);
	});

	const onTimesheetPersonChange = useCallback(personId => {
		handleTabChange(TimesheetTabName.Timesheet);
		setFilters({task: {person: [personId]}});
	}, []);

	useSubscription(EVENT_ID.TIMESHEET_PERSON_CHANGED, onTimesheetPersonChange);

	const usingCurrentUserAssigneeFilter = useMemo(() => {
		return !nonViewerPersonSelected && JSON.stringify(filters) === JSON.stringify({task: {person: [selectedPerson.id]}});
	}, [filters?.task]);

	const weekStart = useMemo(() => currentViewingDate.clone().startOf('week'), [currentViewingDate]);
	const weekEnd = useMemo(() => currentViewingDate.clone().endOf('week'), [currentViewingDate]);

	const holidays = useMemo(() => {
		return isWeekView
			? Array(7)
					.fill(0)
					.map((_, i) => weekStart.clone().add(i, 'day'))
					.map(day => getHolidaysOfTheDay(selectedPerson, day))
			: getHolidaysOfTheDay(selectedPerson, currentViewingDate);
	}, [isWeekView, selectedPerson, currentViewingDate]);

	const isFullyLocked = useMemo(() => {
		if (lockedDate) {
			return isWeekView ? lockedDate.isSameOrAfter(weekEnd, 'd') : lockedDate.isSameOrAfter(currentViewingDate, 'd');
		}
		return false;
	}, [isWeekView, lockedDate, currentViewingDate]);

	const isWorkdaysLocked = useMemo(() => {
		if (lockedDate && lastWorkingDayInWeek) {
			return lockedDate.isSameOrAfter(lastWorkingDayInWeek, 'd');
		}
		return false;
	}, [isWeekView, lockedDate, lastWorkingDayInWeek]);

	const timeRegsForPeriod = useMemo(() => {
		return timeRegistrations.filter(tReg => {
			const tDate = Util.CreateNonUtcMomentDate(tReg.node.year, tReg.node.month, tReg.node.day);
			if (tReg.node.person.id !== selectedPerson.id) {
				return false; // Only show time registrations for the selected person
			}
			if (isWeekView) {
				return tDate?.isSameOrAfter(weekStart, 'days') && tDate.isSameOrBefore(weekEnd, 'days');
			} else {
				return currentViewingDate.isSame(tDate, 'date');
			}
		});
	}, [timeRegistrations, weekStart, weekEnd, isWeekView]);

	// Week-scoped project time-reg predicate — intentionally ignores isWeekView so it always
	// covers the full week. Used by getResubmitDateRange to decide whether to force full-week
	// range on Resubmit (the backend's 0h-path guard requires a full-week range for 0h weeks).
	const hasProjectTimeRegsInWeek = useMemo(() => {
		return timeRegistrations.some(tReg => {
			if (tReg.node.person.id !== selectedPerson.id) return false;
			if (tReg.node.idleTime) return false;
			const tDate = Util.CreateNonUtcMomentDate(tReg.node.year, tReg.node.month, tReg.node.day);
			return tDate?.isSameOrAfter(weekStart, 'days') && tDate.isSameOrBefore(weekEnd, 'days');
		});
	}, [timeRegistrations, weekStart, weekEnd, selectedPerson.id]);

	useEffect(() => {
		if (setHasProjectTimeRegsInWeek) {
			setHasProjectTimeRegsInWeek(hasProjectTimeRegsInWeek);
		}
	}, [hasProjectTimeRegsInWeek, setHasProjectTimeRegsInWeek]);

	// IF we loaded on TimesheetTabName.Timesheet but there are no time entries for the selected date range, switch to TimesheetTabName.FindTasks
	useEffect(() => {
		if (selectedTab === TimesheetTabName.Timesheet && timeRegsForPeriod.length === 0) {
			setSelectedTab(TimesheetTabName.FindTasks);
		}
	}, []);

	const periodTimeApprovalStatus = usingTimeApproval
		? getTimesheetsApprovalStatus(
				timeRegsForPeriod,
				selectedPerson,
				viewer.company,
				currentViewingDate,
				hasFeatureFlag('timesheet_approval_new_statuses'),
				// PIKA-527: forward the person's weekApprovals so 0h-DECLINED / 0h-APPROVED
				// weeks resolve via getApprovalStatusForWeek (web-front/src/forecast-app/shared/util/TimesheetApprovalStatus.ts:54)
				// precedence step 2 (PWA sticky status) instead of falling through to step 3 (lock-based SUBMITTED).
				selectedPerson.weekApprovals ?? []
		  )
		: null;

	useEffect(() => {
		if (setPeriodTimeApprovalStatus && periodTimeApprovalStatus !== null) {
			setPeriodTimeApprovalStatus(periodTimeApprovalStatus);
		}
	}, [periodTimeApprovalStatus, setPeriodTimeApprovalStatus]);

	// TODO: currently shows the first decline reason found — if multiple rows have different reasons,
	// only one surfaces in the tooltip. Decide how to handle multiple distinct decline reasons
	const declineReason = useMemo(() => {
		if (!hasFeatureFlag('timesheet_approval_new_statuses')) return null;
		const fromRow = timeRegsForPeriod?.find(tReg => tReg.node.declineReason)?.node.declineReason;
		if (fromRow) return fromRow;
		// PIKA-527: 0h-DECLINED fallback — read the reason from the per-week PWA row.
		// Matches the same week-key shape getApprovalStatusForWeek uses for precedence step 2
		// (web-front/src/forecast-app/shared/util/TimesheetApprovalStatus.ts:74-79).
		const pwa = (selectedPerson.weekApprovals ?? []).find(
			(r: {weekStartYear: number; weekStartMonth: number; weekStartDay: number; declineReason: string | null}) =>
				r.weekStartYear === weekStart.year() &&
				r.weekStartMonth === weekStart.month() + 1 &&
				r.weekStartDay === weekStart.date()
		);
		return pwa?.declineReason ?? null;
	}, [timeRegsForPeriod, selectedPerson.weekApprovals, weekStart]);

	// Hide suggestions if the dates are changed
	useEffect(() => {
		setAndTrackShowSuggestions(false);
	}, [currentViewingDate, isWeekView]);
	// Hide suggestions if showing someone elses time regs
	useEffect(() => {
		if (nonViewerPersonSelected) {
			setAndTrackShowSuggestions(false);
		}
	}, [nonViewerPersonSelected]);

	const toggleSuggestions = () => {
		trackEvent(showSuggestions ? 'Hide Suggested Entries Button' : 'Reveal Suggested Entries Button', 'Clicked', {
			availableSuggestions: validTimeRegistrationSuggestions?.length,
		});
		setAndTrackShowSuggestions(!showSuggestions);
	};

	const hasSuggestionWithoutTimeReg = useMemo(
		() => suggestionsWithoutTimeRegs(timeRegsForPeriod, validTimeRegistrationSuggestions),
		[validTimeRegistrationSuggestions, timeRegsForPeriod]
	);

	let suggestionTooltip: string | null = null;
	if (!hasSuggestions) {
		suggestionTooltip = formatMessage({id: 'timesheets.tooltip.no_suggestions'});
	} else if (!hasSuggestionWithoutTimeReg) {
		suggestionTooltip = formatMessage({id: 'timesheets.tooltip.no_relevant_suggestions'});
	}

	const onCopyFromLastWeekClicked = () => {
		const copyFullWeek = timePeriod === TIME_PERIOD.WEEK_VIEW;
		if (copyFullWeek) {
			trackEvent('Copy Previous Week Button', 'Clicked');
		} else {
			trackEvent('Copy Previous Day Button', 'Clicked');
		}
		showModal({
			type: MODAL_TYPE.COPY_FROM_LAST_WEEK,
			personId: selectedPersonId,
			selectedDate: currentViewingDate.toDate(),
			copyFullWeek: timePeriod === TIME_PERIOD.WEEK_VIEW,
		});
	};

	const selectDate = useCallback(
		(day: Moment) => {
			setTimePeriod(TIME_PERIOD.DAY_VIEW);
			setCurrentViewingDate(day);
		},
		[setTimePeriod, setCurrentViewingDate]
	);

	return (
		<BottomSectionWrapper data-userpilot="timesheets-task-list" timeRegSideModalOpen={timeRegSideModalOpen}>
			<TabSectionWrapper>
				<FlexRow justifyContent={'space-between'}>
					<FlexRow gap={'xl'} className={'header-bar no-padding header-bar-left'}>
						<Tabs value={selectedTab} onValueChange={handleTabChange}>
							<Tabs.List>
								<TimesheetTab timeRegsForPeriod={timeRegsForPeriod} />
								<InternalTimeTab viewer={viewer} />
								<FindTasksTab taskTypeSearchQuery={taskTypeSearchQuery} />
							</Tabs.List>
						</Tabs>
						<PeriodToggle timePeriod={timePeriod} setTimePeriod={setTimePeriod} />
					</FlexRow>
					<FlexRow justifyContent={'flex-end'} gap={'xs'} className={'header-bar no-padding header-bar-right'}>
						{(selectedTab === TimesheetTabName.FindTasks || selectedTab === TimesheetTabName.Timesheet) && (
							<TheEye
								expandLeft={true}
								key={`the-eye`}
								options={eyeOptions}
								onSelect={(_, __, ___, newOptions) => setEyeOptions(newOptions)}
								useDesignSystemButton={true}
								emphasis={EMPHASIS.MONO}
							/>
						)}
						{selectedTab === TimesheetTabName.FindTasks && (
							<TimesheetsFilters
								filters={filters}
								setFilters={setFilters}
								setSavedFilters={setSavedFilters}
								viewer={viewer}
								emphasis={EMPHASIS.MONO}
							/>
						)}
						{selectedTab === TimesheetTabName.Timesheet && (
							<Button
								id={'copy-from-last-week'}
								data-cy={'copy-from-last-week-button'}
								disabled={isCopyDisabled}
								onPress={onCopyFromLastWeekClicked}
								emphasis={EMPHASIS.MONO}
							>
								{timePeriod === TIME_PERIOD.WEEK_VIEW
									? formatMessage({id: 'timesheets.copy_previous_week'})
									: formatMessage({id: 'timesheets.copy_previous_day'})}
							</Button>
						)}
						{selectedTab === TimesheetTabName.Timesheet && (
							<ForecastTooltip content={suggestionTooltip}>
								<Button
									data-cy={'toggle-suggestions-button'}
									disabled={!hasSuggestionWithoutTimeReg || !hasSuggestions}
									icon={showSuggestions ? 'sparkleHide' : 'sparkle'}
									onPress={toggleSuggestions}
									emphasis={EMPHASIS.MONO}
								>
									{showSuggestions && hasSuggestionWithoutTimeReg
										? formatMessage({id: 'timesheets.suggestions.hide_suggestions'})
										: formatMessage({id: 'timesheets.suggestions.show_suggestions'})}
								</Button>
							</ForecastTooltip>
						)}
					</FlexRow>
				</FlexRow>
			</TabSectionWrapper>
			<InfoSectionWrapper justifyContent={'space-between'}>
				<FlexRow>
					<DateInfoSection
						timePeriod={timePeriod}
						selectedTab={selectedTab}
						currentViewingDate={currentViewingDate}
						weekStart={weekStart}
						weekEnd={weekEnd}
						showAllTimeTasks={showAllTimeTasks}
						holidays={holidays}
					/>
					{allowOutsideTaskDates && selectedTab === TimesheetTabName.FindTasks ? (
						<Button
							icon={'closeS'}
							onPress={() => setShowAllTimeTasks(!showAllTimeTasks)}
							emphasis={EMPHASIS.LOW}
						/>
					) : null}
					{(() => {
						if (!periodTimeApprovalStatus) return null;

						const hasProjectTimeRegs = timeRegsForPeriod?.some(tReg => !tReg.node.idleTime);
						// FF ON: show badge if there are project rows, or if the week is 0h-but-meaningful —
						// SUBMITTED (admin-on-behalf submit) / DECLINED (approver rejected) / APPROVED (approver
						// approved). The last two are 0h sticky states from a person_week_approval row (PIKA-519);
						// before PIKA-527 those rows weren't read on My Timesheets so this gate didn't need them.
						// FF OFF: same as before — isWorkdaysLocked && status != NOT_SUBMITTED.
						const shouldShowBadge = hasFeatureFlag('timesheet_approval_new_statuses')
							? hasProjectTimeRegs ||
							  periodTimeApprovalStatus === APPROVAL_STATUS.SUBMITTED ||
							  periodTimeApprovalStatus === APPROVAL_STATUS.DECLINED ||
							  periodTimeApprovalStatus === APPROVAL_STATUS.APPROVED
							: isWorkdaysLocked && periodTimeApprovalStatus !== APPROVAL_STATUS.NOT_SUBMITTED;

						if (!shouldShowBadge) return null;

						if (hasFeatureFlag('timesheet_approval_new_statuses')) {
							switch (periodTimeApprovalStatus) {
								case APPROVAL_STATUS.APPROVED:
									return (
										<HeaderLabel
											text={formatMessage({id: 'time_approval.approved'})}
											color={INDICATOR_COLOR.GREEN}
										/>
									);
								case APPROVAL_STATUS.SUBMITTED:
									return (
										<HeaderLabel
											text={formatMessage({id: 'time_approval.submitted'})}
											color={INDICATOR_COLOR.BLUE}
										/>
									);
								case APPROVAL_STATUS.DECLINED:
									return (
										<ForecastTooltip content={declineReason || undefined}>
											<HeaderLabel
												text={formatMessage({id: 'time_approval.declined'})}
												color={INDICATOR_COLOR.RED}
											/>
										</ForecastTooltip>
									);
								case APPROVAL_STATUS.PENDING:
								case APPROVAL_STATUS.NOT_SUBMITTED:
									return (
										<HeaderLabel
											text={formatMessage({id: 'time_approval.pending'})}
											color={INDICATOR_COLOR.AMBER}
										/>
									);
								case APPROVAL_STATUS.INVOICED:
									return (
										<HeaderLabel
											text={formatMessage({id: 'time_approval.invoiced'})}
											color={INDICATOR_COLOR.GREY}
										/>
									);
								default:
									return null;
							}
						}

						// Original behaviour (flag off)
						return periodTimeApprovalStatus === APPROVAL_STATUS.APPROVED ? (
							<HeaderLabel text={formatMessage({id: 'time_approval.approved'})} color={INDICATOR_COLOR.GREEN} />
						) : (
							<HeaderLabel text={formatMessage({id: 'time_approval.submitted'})} color={INDICATOR_COLOR.BLUE} />
						);
					})()}
				</FlexRow>
				{selectedTab === TimesheetTabName.FindTasks && savedFilters}
			</InfoSectionWrapper>
			<TableWrapper>
				{selectedTab === TimesheetTabName.FindTasks ? (
					<ForecastQueryRenderer
						key="query-render-TimesheetThisWeeksWorkTableWrapper"
						query={TimesheetThisWeeksWorkTableWrapperQuery}
						customLoader={() => <InlineLoader />}
						fetchPolicy={'network-only'}
						variables={{
							pageSize: TIMESHEET_THIS_WEEKS_WORK_PAGE_SIZE,
							taskTypeSearchQuery: taskTypeSearchQuery,
							sortValue: serialiseSort(sort),
							timePeriod: timePeriod,
							currentViewingDate: currentViewingDate.format('YYYY-MM-DD'),
							personId: selectedPerson.id,
						}}
						render={(relayProps: RelayProps<TimesheetThisWeeksWorkTableWrapper_viewer>) => {
							return (
								<TimesheetThisWeeksWorkTableWrapper
									{...relayProps}
									paginationRef={paginationRef}
									selectedPerson={selectedPerson}
									timeRegistrations={timeRegistrations}
									timeRegsForPeriod={timeRegsForPeriod}
									currentViewingDate={currentViewingDate}
									timePeriod={timePeriod}
									filterActive={!!filters?.task}
									searchActive={searchActive}
									usingCurrentUserAssigneeFilter={usingCurrentUserAssigneeFilter}
									sort={sort}
									selectDate={selectDate}
									holidays={holidays}
									lockedDate={lockedDate}
									isFullyLocked={isFullyLocked}
									usingTimeApproval={usingTimeApproval}
									usingInternalTimeApproval={usingInternalTimeApproval}
									periodTimeApprovalStatus={periodTimeApprovalStatus}
									overBudgetProgramsByCompanyProjectId={overBudgetProgramsByCompanyProjectId}
									timeRegistrationTargetSuggestionsData={timeRegistrationTargetSuggestionsData}
									goToTimesheetsTab={goToTimesheetsTab}
									refetchEntries={refetch}
									customFieldDefinitions={customFieldDefinitions}
								/>
							);
						}}
					/>
				) : selectedTab === TimesheetTabName.InternalTime ? (
					<ForecastQueryRenderer
						key="query-render-TimesheetInternalTimeTableWrapper"
						query={TimesheetInternalTimeTableWrapperQuery}
						customLoader={() => <InlineLoader />}
						fetchPolicy={'network-only'}
						render={(relayProps: RelayProps<TimesheetInternalTimeTableWrapper_viewer>) => {
							return (
								<TimesheetInternalTimeTableWrapper
									{...relayProps}
									selectedPerson={selectedPerson}
									timeRegistrations={timeRegistrations}
									timeRegsForPeriod={timeRegsForPeriod}
									currentViewingDate={currentViewingDate}
									timePeriod={timePeriod}
									sort={sort}
									selectDate={selectDate}
									holidays={holidays}
									lockedDate={lockedDate}
									isFullyLocked={isFullyLocked}
									usingTimeApproval={usingTimeApproval}
									usingInternalTimeApproval={usingInternalTimeApproval}
									periodTimeApprovalStatus={periodTimeApprovalStatus}
									overBudgetProgramsByCompanyProjectId={overBudgetProgramsByCompanyProjectId}
									timeRegistrationTargetSuggestionsData={timeRegistrationTargetSuggestionsData}
									goToTimesheetsTab={goToTimesheetsTab}
									showSuggestions={showSuggestions}
									hasSuggestions={hasSuggestions}
									refetchEntries={refetch}
								/>
							);
						}}
					/>
				) : (
					<ForecastQueryRenderer
						key="query-render-TimesheetTimeTableWrapper"
						query={TimesheetTimeTableWrapperQuery}
						customLoader={() => <InlineLoader />}
						render={(relayProps: RelayProps<TimesheetTimeTableWrapper_viewer>) => {
							return (
								<TimesheetTimeTableWrapper
									{...relayProps}
									selectedPerson={selectedPerson}
									timeRegistrations={timeRegistrations}
									timeRegsForPeriod={timeRegsForPeriod}
									currentViewingDate={currentViewingDate}
									timePeriod={timePeriod}
									sort={sort}
									selectDate={selectDate}
									holidays={holidays}
									lockedDate={lockedDate}
									isFullyLocked={isFullyLocked}
									usingTimeApproval={usingTimeApproval}
									usingInternalTimeApproval={usingInternalTimeApproval}
									periodTimeApprovalStatus={periodTimeApprovalStatus}
									overBudgetProgramsByCompanyProjectId={overBudgetProgramsByCompanyProjectId}
									// The full set of suggestions and the valid suggestions are passed along separately because the tracking requires the full set, but the table itself only requires the valid
									timeRegistrationTargetSuggestionsData={timeRegistrationTargetSuggestionsData}
									validTimeRegistrationSuggestions={validTimeRegistrationSuggestions}
									goToTimesheetsTab={goToTimesheetsTab}
									showSuggestions={showSuggestions}
									hasSuggestions={hasSuggestions}
									refetchEntries={refetch}
									customFieldDefinitions={customFieldDefinitions}
								/>
							);
						}}
					/>
				)}
			</TableWrapper>
		</BottomSectionWrapper>
	);
};
